import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BatteryCharging, Settings2, Wrench, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { waLink } from "@/data/site";

const SERVICES = [
  {
    no: "01",
    icon: BatteryCharging,
    title: "Pil Değişimi",
    desc: "Saatinizin pilini orijinaliyle, dakikalar içinde değiştiriyoruz. Değişim sonrası saatinizin düzgün çalıştığını birlikte kontrol ediyoruz.",
  },
  {
    no: "02",
    icon: Settings2,
    title: "Bakım & Onarım",
    desc: "Her marka kol saatinin mekanizma bakımını ve onarımını özenle yapıyoruz. Önce saatinizi inceliyor, yapılacak işlemi ve ücretini önceden size bildiriyoruz.",
  },
  {
    no: "03",
    icon: Wrench,
    title: "Kordon Değişimi",
    desc: "Deri, çelik ve silikon kordon seçenekleriyle saatinize en uygun kordonu takıyor, bileğinize göre ayarlıyoruz.",
  },
];

const STEPS = [
  { no: "01", title: "Bize Ulaşın", desc: "WhatsApp'tan yazın, saatinizin ihtiyacını kısaca anlatın." },
  { no: "02", title: "Ön İnceleme", desc: "Saatinizi mağazamızda inceliyor, yapılacak işlemi ve ücretini önceden paylaşıyoruz." },
  { no: "03", title: "Teslim", desc: "Onayınız sonrası işlemi tamamlıyor, saatinizi teslim ediyoruz." },
];

export default function TeknikServisPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="service-page">
      <Helmet>
        <title>Teknik Servis | Atılmaz Saat</title>
        <meta
          name="description"
          content="Yaşanmışlıklarınızın hatırasını yaşatıyoruz. Pil değişimi, bakım & onarım ve kordon değişimi — 20 yıllık saatçilik tecrübesiyle Atılmaz Saat teknik servisi, Balıkesir Burda AVM."
        />
      </Helmet>
      {/* Başlık */}
      <section className="relative overflow-hidden bg-emerald text-ivory pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="grain-overlay" />
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none select-none">
          <p className="font-serif text-[18vw] leading-[0.8] font-semibold text-outline-ivory whitespace-nowrap translate-y-[18%]">
            SERVİS
          </p>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ivory/50">Atılmaz Saat</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight uppercase">
              Teknik Servis
            </h1>
            <p className="mt-5 font-serif text-2xl sm:text-3xl italic leading-snug text-gold max-w-2xl">
              "Yaşanmışlıklarınızın hatırasını yaşatıyoruz."
            </p>
            <p className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-ivory/65 font-light">
              Saatiniz yalnızca bir aksesuar değil; anılarınızın taşıyıcısıdır.
              20 yıllık saatçilik tecrübemizle, her marka kol saatine aynı
              özenle bakım ve onarım hizmeti sunuyoruz.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink("Merhaba, teknik servis hizmetleriniz hakkında bilgi almak ve randevu oluşturmak istiyorum.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-page-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-ivory px-8 h-[52px] text-sm font-semibold text-emerald transition-colors duration-300 hover:bg-sand"
              >
                <MessageCircle size={16} />
                Randevu için WhatsApp
              </a>
              <button
                onClick={() => navigate("/")}
                data-testid="service-page-back-link"
                className="inline-flex items-center justify-center gap-3 rounded-full border border-ivory/25 px-8 h-[52px] text-sm font-medium text-ivory transition-colors duration-300 hover:border-ivory/60"
              >
                <ArrowLeft size={16} />
                Ana Sayfaya Dön
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Hizmetler */}
      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-sage">Hizmetlerimiz</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-ink">
              Yaptığımız <span className="italic text-emerald">işler</span>
            </h2>
          </Reveal>
          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.no} delay={i * 0.1}>
                <div
                  data-testid={`service-card-${s.no}`}
                  className="group h-full bg-white border border-gold-muted p-7 sm:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:shadow-emerald/5"
                >
                  <div className="flex items-start justify-between">
                    <span className="h-12 w-12 rounded-full bg-emerald inline-flex items-center justify-center text-ivory">
                      <s.icon size={20} strokeWidth={1.6} />
                    </span>
                    <span className="font-serif text-4xl font-semibold text-emerald/10 leading-none">{s.no}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-sage">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Süreç */}
      <section className="relative overflow-hidden bg-emerald-deep text-ivory py-16 md:py-24">
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ivory/50">Süreç</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight">
              Nasıl <span className="italic text-gold">ilerliyoruz?</span>
            </h2>
          </Reveal>
          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.no} delay={i * 0.1}>
                <div className="border-t border-ivory/15 pt-6" data-testid={`service-step-${s.no}`}>
                  <span className="font-serif text-5xl font-semibold text-ivory/15 leading-none">{s.no}</span>
                  <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/60">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-ivory/15 px-6 py-6 sm:px-10">
              <p className="font-serif text-xl sm:text-2xl font-medium leading-snug max-w-xl">
                Saatiniz bize emanet — <span className="italic text-gold">2006'dan beri aynı özen.</span>
              </p>
              <a
                href={waLink("Merhaba, teknik servis hizmetleriniz hakkında bilgi almak ve randevu oluşturmak istiyorum.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-page-bottom-whatsapp"
                className="shrink-0 inline-flex items-center gap-3 rounded-full bg-ivory px-8 h-12 text-sm font-semibold text-emerald transition-colors duration-300 hover:bg-sand"
              >
                <MessageCircle size={16} />
                Bize Ulaşın
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
